import React, { useEffect, useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../hook/request';
import { Btn, H3 } from '../../../AbstractElements';
import Loader from '../../../Components/Widgets/Loader/generic'
import DeleteConfirm from '../../../Components/Modals/Delete';

type Progetto = {
    ID: number;
    nome: string;
    data_ins: string;
    creato_da: number;
    data_attivazione: string;
    data_chiusura: string;
    cancellato: number;
    abilitato: number;
    descrizione: string;
    nominativo: string;
};

function fmtDate(iso?: string | null) {
    if (!iso) return '-';
    const s = iso.trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return iso;
    const [y, m, d] = s.split(' ')[0].split('T')[0].split('-');
    return `${d}/${m}/${y}`;
}

export default function ProjectsList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<Progetto[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState('');

    async function loadProjects() {
        setLoading(true);
        try {
            const res = await get<any>('/progetti/list');
            const payload = (res as any)?.data ?? res;
            const raw: any[] = Array.isArray(payload) ? payload : [];
            const list: Progetto[] = raw.flat();

            setRows(list);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadProjects();
    }, []);

    const columns: TableColumn<Progetto>[] = useMemo(
        () => [
            {
                name: 'Cliente',
                selector: (r) => r.nominativo ?? '',
                sortable: true,
                grow: 1.5,
            },
            {
                name: 'Nome',
                selector: (r) => r.nome ?? '',
                sortable: true,
                grow: 1.5,
            },
            
            {
                name: 'Data attivazione',
                selector: (r) => r.data_attivazione ?? '',
                sortable: true,
                right: true,
                width: '160px',
                cell: (r) => fmtDate(r.data_attivazione),
            },
            {
                name: 'Data chiusura',
                selector: (r) => r.data_chiusura ?? '',
                sortable: true,
                right: true,
                width: '160px',
                cell: (r) => fmtDate(r.data_chiusura),
            },
            {
                name: 'Abilitato',
                selector: (r) => String(r.abilitato),
                center: true,
                width: '120px',
                cell: (r) => (r.abilitato === 1 ? '✅' : '❌'),
            },
            {
                name: 'Azioni',
                width: '180px',
                button: true,
                ignoreRowClick: true,
                cell: (r) => (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-info"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/progetti/${r.ID}`);
                            }}
                            title='apri...'
                        >
                            <i className="fa-solid fa-arrow-up-right-from-square"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/gestione-progetti/progetti/${encodeURIComponent(String(r.ID))}`);
                            }}
                            title="Modifica"
                        >
                            Modifica
                        </button>
                        <DeleteConfirm
                            url={`/progetti/progetto?ID=${r.ID}`}
                            onSuccess={() => {
                                loadProjects();
                            }}
                            trigger={
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    title="Elimina"
                                >
                                    Elimina
                                </button>
                            }
                        />

                    </div>

                ),
            },
        ],
        [navigate]
    );


    const filteredRows = useMemo(() => {
        const term = filterText.trim().toLowerCase();
        if (!term) return rows;

        return rows.filter((r) => {
            const nome = r.nome?.toLowerCase() ?? '';
            const descrizione = r.descrizione?.toLowerCase() ?? '';
            return nome.includes(term) || descrizione.includes(term);
        });
    }, [rows, filterText]);

    return (
        <div className="page-body pt-4">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <H3 className="mb-0">Progetti</H3>
                        <Btn
                            className="px-4"
                            color="primary"
                            type="button"
                            onClick={() => navigate('/gestione-progetti/progetti/new')}
                        >
                            NUOVO
                        </Btn>
                    </div>

                    <div className="mb-3 d-flex justify-content-between">
                        <input
                            type="text"
                            className="form-control w-50"
                            placeholder="Filtra per nome o descrizione..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </div>

                    <div className="table-responsive">
                        <DataTable
                            columns={columns}
                            data={filteredRows}
                            progressPending={loading}
                            progressComponent={<Loader />}
                            striped
                            pagination
                            highlightOnHover
                            dense
                            pointerOnHover
                            onRowClicked={(row) => navigate(`/gestione-progetti/progetti/${row.ID}`)}
                            noDataComponent="Nessun progetto disponibile"
                            paginationPerPage={50}
                            customStyles={{
                                rows: {
                                    style: {
                                        minHeight: '30px',
                                        cursor: 'pointer',
                                    },
                                },
                            }}
                        />
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}
